import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import ImageUpload from "../../common/ImageUpload";

const IMPACT_FIELDS = [
    { id: "tree", label: "Tree" },
    { id: "co2", label: "CO2" },
    { id: "energy", label: "Energy" },
    { id: "water", label: "Water" },
    { id: "item_recycled", label: "Item Recycled" },
];

export default function PledgeModal({ show, onClose, onSubmit, initialData, isLoading }) {
    const [formData, setFormData] = useState({
        text: "",
        icon: "",
        isActive: true,
        sort: 1,
        tree: 0,
        co2: 0,
        energy: 0,
        water: 0,
        item_recycled: 0,
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (show) {
            if (initialData) {
                setFormData({
                    text: initialData.text || initialData.title || "",
                    icon: initialData.icon || "",
                    isActive: initialData.isActive ?? true,
                    sort: initialData.sort || 1,
                    tree: initialData.tree || 0,
                    co2: initialData.co2 || 0,
                    energy: initialData.energy || 0,
                    water: initialData.water || 0,
                    item_recycled: initialData.item_recycled || 0,
                });
            } else {
                setFormData({
                    text: "",
                    icon: "",
                    isActive: true,
                    sort: 1,
                    tree: 0,
                    co2: 0,
                    energy: 0,
                    water: 0,
                    item_recycled: 0,
                });
            }
            setErrors({});
        }
    }, [show, initialData]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.text?.trim()) newErrors.text = "Pledge text is required.";
        if (!formData.icon) newErrors.icon = "Image icon is required.";
        if (formData.sort === "" || formData.sort < 1) newErrors.sort = "Valid sort order is required.";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : (type === "number" ? Number(value) : value)
        }));

        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            onSubmit(formData);
        }
    };

    return (
        <Modal show={show} onHide={onClose} centered size="lg">
            <Modal.Header closeButton className="bg-light">
                <Modal.Title>{initialData ? "Edit Pledge" : "Add Pledge"}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body className="p-4">
                    <Row>
                        <Col md={6}>
                            <h5 className="mb-2 text-primary">Basic Info</h5>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Pledge Text <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    name="text"
                                    value={formData.text}
                                    onChange={handleInputChange}
                                    isInvalid={!!errors.text}
                                    placeholder="Enter your full pledge description here..."
                                    style={{ resize: "none" }}
                                />
                                <Form.Control.Feedback type="invalid">{errors.text}</Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <ImageUpload
                                    label={<>Pledge Icon <span className="text-danger">*</span></>}
                                    value={formData.icon}
                                    uploadEndpoint="/upload/pledgeimage"
                                    error={errors.icon}
                                    onChange={(newUrl) => setFormData(prev => ({ ...prev, icon: newUrl }))}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Sort Order <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="number"
                                    name="sort"
                                    value={formData.sort}
                                    onChange={handleInputChange}
                                    isInvalid={!!errors.sort}
                                    min="1"
                                />
                                <Form.Control.Feedback type="invalid">{errors.sort}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <h5 className="mb-3 text-primary">Impact Values</h5>
                            <div className="p-2 bg-light rounded border">
                                {IMPACT_FIELDS.map((field) => (
                                    <Form.Group key={field.id} className="mb-2">
                                        <Form.Label className="fw-bold">{field.label}</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name={field.id}
                                            size="sm"
                                            value={formData[field.id]}
                                            onChange={handleInputChange}
                                            placeholder={`Value for ${field.label}`}
                                        />
                                    </Form.Group>
                                ))}
                            </div>
                        </Col>
                    </Row>

                    <div className="d-flex align-items-center rounded mt-2">
                        <Form.Check
                            type="switch"
                            id="active-switch"
                            name="isActive"
                            label={formData.isActive ? "Status: Active" : "Status: Inactive"}
                            checked={formData.isActive}
                            onChange={handleInputChange}
                            className="fw-bold"
                            style={{ cursor: "pointer" }}
                        />
                    </div>

                </Modal.Body>
                <Modal.Footer className="bg-light">
                    <Button variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm mr-2"></span>
                                Saving...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save mr-2"></i>
                                {initialData ? "Update Pledge" : "Create Pledge"}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}